import { useState, useEffect, useCallback } from 'react';

export interface ActivityEvent {
  id: string;
  type: 'journal' | 'ledger' | 'declaration' | 'export' | 'import' | 'validation' | 'calculation' | 'user_action';
  action: string;
  description: string;
  user?: string;
  timestamp: Date;
  status: 'success' | 'warning' | 'error' | 'pending';
  module: string;
  metadata?: Record<string, any>;
}

interface ActivityStore {
  activities: ActivityEvent[];
  addActivity: (activity: Omit<ActivityEvent, 'id' | 'timestamp'>) => void;
  markActivityComplete: (id: string, status: 'success' | 'error') => void;
  getRecentActivities: (count?: number) => ActivityEvent[];
  clearActivities: () => void;
}

// Store global pour les activités (en production, utiliser Zustand ou Redux)
class ActivityManager {
  private activities: ActivityEvent[] = [];
  private listeners: ((activities: ActivityEvent[]) => void)[] = [];
  
  addActivity(activity: Omit<ActivityEvent, 'id' | 'timestamp'>) {
    const newActivity: ActivityEvent = {
      ...activity,
      id: `activity_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date()
    };
    
    this.activities.unshift(newActivity);
    
    // Garder seulement les 100 dernières activités
    if (this.activities.length > 100) {
      this.activities = this.activities.slice(0, 100);
    }
    
    this.notifyListeners();
  }
  
  markActivityComplete(id: string, status: 'success' | 'error') {
    const activityIndex = this.activities.findIndex(a => a.id === id);
    if (activityIndex !== -1) {
      this.activities[activityIndex] = {
        ...this.activities[activityIndex],
        status
      };
      this.notifyListeners();
    }
  }
  
  getActivities(): ActivityEvent[] {
    return [...this.activities];
  }
  
  getRecentActivities(count = 10): ActivityEvent[] {
    return this.activities.slice(0, count);
  }
  
  clearActivities() {
    this.activities = [];
    this.notifyListeners();
  }
  
  subscribe(listener: (activities: ActivityEvent[]) => void) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
  
  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.getActivities()));
  }
}

// Instance globale du gestionnaire d'activités
const activityManager = new ActivityManager();

// Hook pour utiliser le système d'activités
export function useActivities(): ActivityStore {
  const [activities, setActivities] = useState<ActivityEvent[]>([]);
  
  useEffect(() => {
    // S'abonner aux changements d'activités
    const unsubscribe = activityManager.subscribe(setActivities);
    
    // Charger les activités initiales
    setActivities(activityManager.getActivities());
    
    return unsubscribe;
  }, []);
  
  const addActivity = useCallback((activity: Omit<ActivityEvent, 'id' | 'timestamp'>) => {
    activityManager.addActivity(activity);
  }, []);
  
  const markActivityComplete = useCallback((id: string, status: 'success' | 'error') => {
    activityManager.markActivityComplete(id, status);
  }, []);
  
  const getRecentActivities = useCallback((count = 10) => {
    return activityManager.getRecentActivities(count);
  }, []);
  
  const clearActivities = useCallback(() => {
    activityManager.clearActivities();
  }, []);
  
  return {
    activities,
    addActivity,
    markActivityComplete,
    getRecentActivities,
    clearActivities
  };
}

// Hook spécialisé pour enregistrer automatiquement les activités de journal
export function useJournalActivity() {
  const { addActivity } = useActivities();
  
  const logJournalEntry = useCallback((action: string, description: string, amount?: number) => {
    addActivity({
      type: 'journal',
      action,
      description: amount ? `${description} - ${amount.toLocaleString('fr-FR')} CDF` : description,
      user: 'Admin', // En production, récupérer de useAuth()
      status: 'success',
      module: 'Journal',
      metadata: { amount }
    });
  }, [addActivity]);
  
  const logJournalValidation = useCallback((description: string, count: number) => {
    addActivity({
      type: 'validation',
      action: 'Validation en lot ADHA',
      description: `${count} écritures validées - ${description}`,
      user: 'ADHA AI',
      status: 'success',
      module: 'Agent Comptable',
      metadata: { count }
    });
  }, [addActivity]);
  
  const logJournalExport = useCallback((format: string, period: string) => {
    addActivity({
      type: 'export',
      action: `Export ${format.toUpperCase()} généré`,
      description: `Journal des écritures - ${period}`,
      user: 'Admin',
      status: 'success',
      module: 'Export',
      metadata: { format, period }
    });
  }, [addActivity]);
  
  return {
    logJournalEntry,
    logJournalValidation,
    logJournalExport
  };
}

// Hook pour les activités du grand livre
export function useLedgerActivity() {
  const { addActivity } = useActivities();
  
  const logLedgerConsultation = useCallback((account: string, accountName: string) => {
    addActivity({
      type: 'ledger',
      action: 'Grand livre consulté',
      description: `Compte ${account} - ${accountName}`,
      user: 'Admin',
      status: 'success',
      module: 'Grand Livre',
      metadata: { account, accountName }
    });
  }, [addActivity]);
  
  const logLedgerExport = useCallback((account: string, format: string) => {
    addActivity({
      type: 'export',
      action: `Export grand livre ${format.toUpperCase()}`,
      description: `Compte ${account}`,
      user: 'Admin',
      status: 'success',
      module: 'Grand Livre',
      metadata: { account, format }
    });
  }, [addActivity]);
  
  return {
    logLedgerConsultation,
    logLedgerExport
  };
}

// Hook pour les activités de déclarations
export function useDeclarationActivity() {
  const { addActivity } = useActivities();
  
  const logDeclarationCreated = useCallback((type: string, period: string) => {
    addActivity({
      type: 'declaration',
      action: `Déclaration ${type} préparée`,
      description: `${period}`,
      user: 'Admin',
      status: 'success',
      module: 'Déclarations',
      metadata: { type, period }
    });
  }, [addActivity]);
  
  const logDeclarationSubmitted = useCallback((type: string, period: string) => {
    addActivity({
      type: 'declaration',
      action: `Déclaration ${type} soumise`,
      description: `${period} - Transmise aux autorités fiscales`,
      user: 'Admin',
      status: 'success',
      module: 'Déclarations',
      metadata: { type, period }
    });
  }, [addActivity]);
  
  return {
    logDeclarationCreated,
    logDeclarationSubmitted
  };
}

// Fonctions utilitaires pour l'historique des activités
export function getActivityIcon(type: ActivityEvent['type']) {
  const iconMap = {
    journal: 'FileText',
    ledger: 'BookOpen', 
    declaration: 'FileSpreadsheet',
    export: 'Download',
    import: 'Upload',
    validation: 'CheckCircle',
    calculation: 'Calculator',
    user_action: 'Users'
  };
  
  return iconMap[type] || 'Edit';
}

export function getActivityColor(status: ActivityEvent['status']) {
  const colorMap = {
    success: 'text-green-600 bg-green-50 dark:bg-green-900/20',
    warning: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20', 
    error: 'text-red-600 bg-red-50 dark:bg-red-900/20',
    pending: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20'
  };
  
  return colorMap[status] || 'text-gray-600 bg-gray-50 dark:bg-gray-900/20';
}

export function getTimeAgo(timestamp: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - timestamp.getTime();
  const diffInMin = Math.floor(diffInMs / (1000 * 60));
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  
  if (diffInMin < 1) {
    return 'À l\'instant';
  } else if (diffInMin < 60) {
    return `Il y a ${diffInMin} min`;
  } else if (diffInHours < 24) {
    return `Il y a ${diffInHours}h`;
  } else if (diffInDays < 7) {
    return `Il y a ${diffInDays}j`;
  } else {
    return timestamp.toLocaleDateString('fr-FR');
  }
}
